import { Box, Tab, Tabs } from "@mui/material";
import { useEffect, useState } from "react";

import axios from "axios";
import { useQuery } from "react-query";
import { nuclearn_api_url } from "src/App";
import TaskTracker from "./TaskTracker";
import ToolTrackerResult from "./ToolTrackerResult/ToolTrackerResult";

export type ToolEventStatus = {
  id: string;
  task_id?: string;
  title: string;
  message?: string;
  status: "pending" | "running" | "completed" | "failed";
  children: ToolEventStatus[];
};

export type ToolEventMetadata = {
  correlation_id: string;
  status: string;
  error: string | null;
  events: ToolEventStatus[];
  current_event: string | null;
  result: string | null;
  progress_percentage: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  title?: string;
  event_type?: string;
  user_id?: string;
};

const ToolTrackerView = ({ taskData }: { taskData: ToolEventMetadata }) => {
  const [tabValue, setTabValue] = useState(0);

  const fetchEventTaskStatus = async (): Promise<ToolEventMetadata> => {
    try {
      const response = await axios.get(
        `${nuclearn_api_url}/events/status/${taskData.correlation_id}`,
      );

      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch task status");
    }
  };

  const {
    data: taskResult,
    isError: isTaskResultError,
    error: taskResultError,
    isLoading: isTaskResultLoading,
  } = useQuery<ToolEventMetadata, Error>(
    ["task-status", taskData.correlation_id],
    fetchEventTaskStatus,
    {
      retry: 1,
      refetchInterval: (data, query) => {
        if (
          query.state.error ||
          data?.status === "completed" ||
          data?.status === "failed"
        ) {
          return false;
        }
        return 3000;
      },
      refetchIntervalInBackground: true,
      enabled: !!taskData.correlation_id,
      onSettled: (data) => {
        if (data?.status === "completed" || data?.status === "failed") {
          return false;
        }
      },
    },
  );

  // Helper function to extract result from events array
  const extractResultFromEvents = (
    events: ToolEventStatus[],
  ): string | null => {
    const findResultEvent = (
      eventList: ToolEventStatus[],
    ): ToolEventStatus | null => {
      for (const event of eventList) {
        if (event.title === "Result") {
          return event;
        }
        if (event.children && event.children.length > 0) {
          const childResult = findResultEvent(event.children);
          if (childResult) return childResult;
        }
      }
      return null;
    };

    const resultEvent = findResultEvent(events);
    return resultEvent?.message || null;
  };

  // Get the actual result - either from top-level result or from events
  const getActualResult = (): string | null => {
    if (taskResult?.result) {
      return taskResult.result;
    }
    if (taskResult?.events && Array.isArray(taskResult.events)) {
      return extractResultFromEvents(taskResult.events);
    }
    return null;
  };

  useEffect(() => {
    const actualResult = getActualResult();
    if (actualResult && taskResult?.status === "completed") {
      setTabValue(1);
    }
  }, [taskResult]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="basic tabs example"
        >
          <Tab label="Status" value={0} />
          <Tab label="Result" value={1} />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box sx={{ p: 2 }}>
          <TaskTracker
            isLoading={isTaskResultLoading}
            taskResult={taskResult}
          />
        </Box>
      )}
      {tabValue === 1 && (
        <Box sx={{ bgcolor: "background.paper", py: 2, px: 4 }}>
          <ToolTrackerResult
            result={getActualResult()}
            isError={isTaskResultError}
            isLoading={isTaskResultLoading}
            error={taskResultError}
          />
        </Box>
      )}
    </Box>
  );
};

export default ToolTrackerView;
